"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.makeRefinement = exports.makeMatchers = exports.makeMatchPI = exports.makeMatchI = exports.makeMatchP = exports.makeMatch = void 0;
/**
 * Pattern matching for a sum type defined with ADT
 *
 * ```ts
 * declare const foo: Option<string>
 *
 * pipe(
 *   foo,
 *   makeMatch("_tag")({
 *     none: () => 'none',
 *     some: ({value}) => 'some'
 *   })
 * )
 * ```
 */
function makeMatch(d) {
    return function (matchObj) { return function (v) { return matchObj[v[d]](v); }; };
}
exports.makeMatch = makeMatch;
/**
 * Partial pattern matching for a sum type defined with ADT
 *
 * ```ts
 * declare const foo: Option<string>
 *
 * pipe(
 *   foo,
 *   makeMatchP("_tag")({
 *     some: ({value}) => 'some'
 *   }, (_option) => 'none')
 * )
 * ```
 */
function makeMatchP(d) {
    return function (matchObj, otherwise) { return function (v) {
        return matchObj[v[d]] != null ? matchObj[v[d]](v) : otherwise(v);
    }; };
}
exports.makeMatchP = makeMatchP;
/**
 * Inverted version of match, useful for better inference in some circumstances
 *
 * ```ts
 * declare const foo: Option<string>
 *
 * makeMatchI('_tag')(foo)({
 *   none: () => 'none',
 *   some: ({value}) => 'some'
 * })
 * ```
 */
function makeMatchI(d) {
    return function (v) { return function (matchObj) { return matchObj[v[d]](v); }; };
}
exports.makeMatchI = makeMatchI;
/**
 * Inverted version of {@link makeMatchP}, useful for better inference in some circumstances
 *
 * ```ts
 * declare const foo: Option<string>
 *
 * makeMatchPI("_tag")(foo)({
 *   some: ({value}) => 'some'
 * }, (_option) => 'none')
 * ```
 */
function makeMatchPI(d) {
    return function (v) { return function (matchObj, otherwise) {
        return matchObj[v[d]] != null
            ? matchObj[v[d]](v)
            : otherwise(v);
    }; };
}
exports.makeMatchPI = makeMatchPI;
/**
 * Generate match functions that switch on a specified discriminant field
 *
 * ```ts
 * import * as O from "fp-ts/Option";
 *
 * const [match, matchP, matchI, matchPI] = makeMatchers("_tag");
 *
 * pipe(
 *   O.fromNullable("value"),
 *   match({
 *     None: () => 0,
 *     Some: (v) => v.value,
 *   })
 * );
 * ```
 *
 * @param d the discriminant field to use
 */
exports.makeMatchers = function (d) {
    return [makeMatch(d), makeMatchP(d), makeMatchI(d), makeMatchPI(d)];
};
/**
 * Generate a tag refinement function on a specified discriminant field
 *
 * ```ts
 * import * as T from "fp-ts/These";
 *
 * const refinement = makeRefinement("_tag");
 *
 * declare const foo: T.These<number, string>
 *
 * // error if the tags are misspelled
 * if(refinement(['Left', 'Both'])(foo)) {
 *   console.log(foo.left)
 * }
 * ```
 *
 * @param d the discriminant field to use
 * @param tags the tags to refine on
 * @param v the ADT to refine
 */
exports.makeRefinement = function (d) { return function () {
    var tags = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        tags[_i] = arguments[_i];
    }
    return function (v) { return tags.indexOf(v[d]) > -1; };
}; };
//# sourceMappingURL=MakeADT.js.map